import { type ContentTableRowData, ROW_TYPE } from 'features/traffic/campaigns/campaign-configs/types';

/**
 * Represents a formatting request with all context needed
 */
export type FormatRequest = {
  value: unknown;
  schema: JsonSchemaProperty | undefined;
  path: string[];
  config: ContentTableConfig;
};

/**
 * Represents a row type determination request
 */
export type RowTypeDeterminationRequest = {
  schema: JsonSchemaProperty | undefined;
  path: string[];
  config: ContentTableConfig;
  value?: string | null;
};

/**
 * Property types in JSON schema
 */
export type SchemaPropertyType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null';

/**
 * Represents a JSON schema property definition
 */
export type JsonSchemaProperty = {
  type?: SchemaPropertyType;
  title?: string;
  format?: string;
  properties?: Record<string, JsonSchemaProperty>;
  items?: JsonSchemaProperty & {
    $ref?: string;
  };
  definitions?: Record<string, JsonSchemaProperty>;
  $ref?: string;
  hidden?: boolean; // Custom property to hide specific fields
};

/**
 * Context for schema processing operations
 */
export type SchemaProcessingContext = {
  rootSchema: JsonSchemaProperty;
  config: ContentTableConfig;
  result: ContentTableRowData[];
};

/**
 * Context for path and property information
 */
export type PropertyContext = {
  path: string[];
  title: string;
  indexSuffix: string;
};

/**
 * Types of patterns for property exclusion
 */
export type ExclusionPattern = string;

/**
 * Configuration for content table data generation
 */
export type ContentTableConfig = {
  /**
   * Patterns for properties to exclude from rendering
   * Can be:
   * - Property name (e.g., 'categoryName')
   * - JSONPath expressions (e.g., '$.properties.arguments.properties.widgetType')
   */
  excludeProperties?: ExclusionPattern[];

  /**
   * Custom formatters for values
   * Key can be:
   * - Property name (e.g., 'categoryName')
   * - Property path (e.g., 'arguments.widgetType')
   * - Property type (e.g., 'boolean')
   */
  valueFormatters?: Record<string, ValueFormatter>;

  /**
   * Custom row type determiners
   * Key can be:
   * - Property name
   * - Property path
   * - Property type
   */
  rowTypeDeterminers?: Record<string, RowTypeDeterminer>;
};

/**
 * Result type for value formatters that can either return a string value or custom row data
 */
export type ValueFormatterResult =
  | string
  | null
  | {
      row?: ContentTableRowData;
      skipProcessing?: boolean;
    };

/**
 * Function signature for custom value formatting
 */
export type ValueFormatter = (value: unknown, propSchema: JsonSchemaProperty, path: string[]) => ValueFormatterResult;

/**
 * Function signature for custom row type determination
 */
export type RowTypeDeterminer = (propSchema: JsonSchemaProperty, path: string[]) => ROW_TYPE;

/**
 * Default config with configurable options
 */
export const defaultConfig: ContentTableConfig = {
  excludeProperties: [
    'widgetName',
    'schema',
    'widgetVersion',
  ],
  rowTypeDeterminers: {},
  valueFormatters: {
    // TODO: These are just example value formatters to show that we can format object or value in whatever way we want. Remove these later
    categoryName: (value) => `Category: ${value}`,
    topLevelLink: (value) => {
      const linkObj = value as {
        label: string;
        url: string;
      };
      // Return a custom row configuration instead of just a string
      return {
        row: {
          label: 'Link',
          secondaryText: linkObj.label,
          type: ROW_TYPE.LINK,
          value: `amazon.com${linkObj.url}`,
        },
        skipProcessing: true, // Skip default property processing
      };
    },
  },
};
